'use client';

import { useMemo, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import {
  Filter,
  FilteredCounter,
  LifecycleFilter,
  ListPageFilterPanel,
  formatCategoryForDisplay,
  formatFindingStatusForDisplay,
} from '@terra/ui-design-system';
import {
  FindingSeveritySchema,
  FindingStatusSchema,
  getSignalCategoriesForTargetType,
} from '@terra/shared-schemas-orchestrator';
import { useTargetType } from '../../../store/projectStore';
import { CreatedSinceFilterPill } from '../../common/CreatedSinceFilterPill';
import { ListPageDateRangeFilter } from '../../common/ListPageDateRangeFilter';
import { pickFilterOptions } from '../../common/list-page-filter-options';
import type { FindingsListFilters } from '../findings-table/useFindingsListFilters';
import {
  resolveFindingsPublicationChip,
  toFindingsPublishedFilter,
} from '../findings-table/findings-list.model';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { FindingsAdvancedFilters } from './FindingsAdvancedFilters';
import { SeverityChangeRequestsFilterChip } from './SeverityChangeRequestsFilterChip';

const SEVERITY_ITEMS = FindingSeveritySchema.options.map((severity) => ({
  value: severity,
  label: severity,
}));
const STATUS_ITEMS = FindingStatusSchema.options.map((status) => ({
  value: status,
  label: formatFindingStatusForDisplay(status),
}));

type FindingsFilterPanelProps = {
  selection?: ReactNode;
  filters: FindingsListFilters;
  filteredCount: number | null;
  totalCount: number | null;
};

export function FindingsFilterPanel({
  selection,
  filters,
  filteredCount,
  totalCount,
}: FindingsFilterPanelProps) {
  const t = useTranslations('designSystem.findingsList.table');
  const tFilters = useTranslations('proxy.filters');
  const tEntities = useTranslations('proxy.filteredCounter.entities');
  const targetType = useTargetType();
  const { isFeatureFlagEnabled: isSeverityChangeRequestEnabled } =
    useFeatureFlags('SEVERITY_CHANGE_REQUEST_ENABLED');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const categoryOptions = useMemo(
    () => getSignalCategoriesForTargetType(targetType),
    [targetType],
  );
  const categoryItems = useMemo(
    () =>
      categoryOptions.map((category) => ({
        value: category,
        label: formatCategoryForDisplay(category),
      })),
    [categoryOptions],
  );
  const hasAdvancedFilters =
    filters.publishedFilter === 'auto-published' ||
    filters.hasPreventionFilter !== 'ALL';

  return (
    <ListPageFilterPanel
      selection={selection}
      isClearDisabled={!filters.hasActiveFilters}
      onClear={() =>
        filters.resetFilters({
          publishedFilter: toFindingsPublishedFilter(
            resolveFindingsPublicationChip(filters.publishedFilter),
          ),
          sortModel: filters.sortModel,
        })
      }
      addFilter={{
        isActive: isAdvancedOpen || hasAdvancedFilters,
        onClick: () => setIsAdvancedOpen((isOpen) => !isOpen),
      }}
      trailing={
        filters.hasActiveFilters ? (
          <FilteredCounter
            filteredCount={filteredCount ?? undefined}
            totalCount={totalCount ?? undefined}
            entityLabel={tEntities('findings')}
          />
        ) : null
      }
      expandedContent={
        isAdvancedOpen || hasAdvancedFilters ? (
          <FindingsAdvancedFilters filters={filters} />
        ) : null
      }
    >
      <Filter
        id="severity"
        density="compact"
        isMultiSelect
        placeholder={t('severity')}
        items={SEVERITY_ITEMS}
        value={filters.severityFilter}
        onChange={(values) =>
          filters.setSeverityFilter(
            pickFilterOptions({
              options: FindingSeveritySchema.options,
              values,
            }),
          )
        }
      />
      <Filter
        id="status"
        density="compact"
        isMultiSelect
        placeholder={t('status')}
        items={STATUS_ITEMS}
        value={filters.statusFilters}
        onChange={(values) =>
          filters.setStatusFilters(
            pickFilterOptions({ options: FindingStatusSchema.options, values }),
          )
        }
      />
      {isSeverityChangeRequestEnabled ? (
        <SeverityChangeRequestsFilterChip filters={filters} />
      ) : null}
      <Filter
        id="category"
        density="compact"
        isMultiSelect
        placeholder={t('category')}
        items={categoryItems}
        value={filters.categoryFilter}
        onChange={(values) =>
          filters.setCategoryFilter(
            pickFilterOptions({ options: categoryOptions, values }),
          )
        }
      />
      <CreatedSinceFilterPill
        createdSince={filters.createdSince}
        onClear={() => filters.setCreatedSince(null)}
      />
      <ListPageDateRangeFilter
        label={tFilters('createdAt')}
        startDate={filters.createdStartDate}
        endDate={filters.createdEndDate}
        onStartDateChange={filters.setCreatedStartDate}
        onEndDateChange={filters.setCreatedEndDate}
      />
      {filters.lifecycle.showLifecycleSelector ? (
        <LifecycleFilter
          value={filters.lifecycleState}
          onValueChange={filters.setLifecycleState}
        />
      ) : null}
    </ListPageFilterPanel>
  );
}
